import { WidgetWithScheduler } from '../WidgetWithScheduler';
import {
    ReachAPIGetTickerFeedResponse,
    TickerFeedItem,
    TickerFeed,
    TickerTemplateData,
    TickerWidgetConfiguration,
    TickerWidgetConfigurationDetails,
    ZodTickerWidgetConfigurationDetails,
} from './types';
import { BSTickerWrapper } from '../../Platform/BrightSign/BSTickerWrapper';
import { ReachAPI } from '../../ReachApi';
import { glom } from '../../Signage/glom';
import { TimelineSlot, WidgetState, WidgetType } from '../types';
import { Signage } from '../../Signage';
import { Log } from '../../AuxillaryService/Log';
import * as Platform from '../../Platform';
import { $ } from '../../../vendor/jquery';
import '../../../vendor/jquery/jquery.webticker-2.1.1~REACH1';
import { TemplateWidgetMixin } from '../../Template';
import { getZoneById } from '../../Display';
import './ticker.scss';

export class TickerWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.TICKER = WidgetType.TICKER;
    declare config: TickerWidgetConfigurationDetails;

    protected schedulerRunInterval = 900;

    /**
     * String value of the ticker's JSON RSS feed
     * @private
     */
    #feedString: undefined | string = undefined;

    /**
     * Does this widget use the BrightSign hardware ticker?
     * @private
     */
    #useHardwareTicker = false;

    /**
     * Object containing the BrightSign Ticker
     * @private
     */
    #brightSignTicker: null | BSTickerWrapper = null;

    /**
     * Is the ticker ready to be rendered
     * @private
     */
    #tickerShouldBeRendered = false;

    /**
     * Compiled ticker items
     * @private
     */
    #tickerItems: TickerFeedItem[] = [];

    /**
     * @param widgetConfiguration
     * @param zoneId
     */
    constructor(widgetConfiguration: TickerWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
    }

    /**
     * Strip HTML from ticker data
     *
     * @param html string of HTML
     * @private
     */
    private decodeHtml(html: string): string {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;

        if (this.config.text_only === 'true') {
            const div = document.createElement('div');
            div.innerHTML = txt.value;
            return div.textContent || '';
        }

        return txt.value;
    }

    /**
     * @inheritDoc
     */
    async initialize(): Promise<void> {
        await super.initialize();

        //todo: replace this check with a player permission check
        if (
            this.config.disable_hardware !== 'true' &&
            Signage.platform instanceof Platform.BrightSignPlayer
        ) {
            this.#useHardwareTicker = true;
        }
    }

    protected validateConfigurationDetails(
        maybeTickerWidgetConfigurationDetails: TickerWidgetConfigurationDetails
    ): TickerWidgetConfigurationDetails {
        return ZodTickerWidgetConfigurationDetails.parse(maybeTickerWidgetConfigurationDetails);
    }

    /**
     * @inheritDoc
     * @protected
     */
    protected async _doPause(): Promise<void> {
        if (!this.#tickerShouldBeRendered && !this.#useHardwareTicker && this.#feedString) {
            this.#tickerShouldBeRendered = true;

            this.renderTicker();
        }

        if (this.#useHardwareTicker && this.#brightSignTicker && this.#brightSignTicker.bsTicker) {
            this.#brightSignTicker.stop();
        }

        if (!this.#useHardwareTicker && this.#feedString) {
            try {
                $(`#webticker_${this.id}`).webTicker('stop');
                //todo: this webTicker is custom JQuery, which we'll eventually need to remove
            } catch (error) {
                Log.Warning(
                    'Unable to pause ticker. Template may not be loaded properly.',
                    null,
                    __fileline,
                    error
                );
            }
        }
    }

    /**
     * @inheritDoc
     */
    async play(currentSlot: TimelineSlot): Promise<void> {
        await super.play(currentSlot);

        if (!this.#feedString) {
            this.state = WidgetState.PAUSED;
            return;
        }

        this.#tickerShouldBeRendered = true;

        if (!this.#useHardwareTicker) {
            try {
                $(`#webticker_${this.id}`).webTicker('cont');
                //todo: this webTicker is custom JQuery, which we'll eventually need to remove
            } catch (error) {
                Log.Warning(
                    'Unable to resume ticker. Template may not be loaded properly.',
                    null,
                    __fileline,
                    error
                );
            }
        } else if (this.#brightSignTicker && !this.#brightSignTicker.bsTicker) {
            this.#brightSignTicker.start();
        }

        this.renderTicker();
    }

    /**
     * @inheritDoc
     */
    async getReadyForDisplay(): Promise<void> {
        return this.schedulerAction();
    }

    /**
     * @inheritDoc
     */
    async stop(): Promise<void> {
        this.#tickerShouldBeRendered = false;

        if (!this.#useHardwareTicker) {
            try {
                $(`#webticker_${this.id}`).webTicker('stop');
                //todo: this webTicker is custom JQuery, which we'll eventually need to remove
            } catch (error) {
                Log.Warning(
                    'Unable to stop ticker. Template may not be loaded properly.',
                    null,
                    __fileline,
                    error
                );
            }
        } else if (this.#brightSignTicker && this.#brightSignTicker.bsTicker) {
            this.#brightSignTicker.stop();
        }

        return super.stop();
    }

    /**
     * Load the RSS data for the ticker on a scheduled basis
     */
    async schedulerAction(): Promise<void> {
        return ReachAPI.rss
            .getFeedForWidget(
                this.id,
                100,
                new ReachAPI.Security(glom.playerUuid, glom.securityToken)
            )
            .then((rssData: ReachAPIGetTickerFeedResponse) => {
                this.hasContentToPlay = true;

                if (!rssData.feed.items) {
                    rssData.feed.items = [];
                    this.hasContentToPlay = false;
                }

                const feedJson = { channel: { item: rssData.feed.items } };
                const newFeedString = JSON.stringify(feedJson);

                // stories_count and total_stories used by javascript in templates
                // stored in database
                // todo: this shouldn't be using window object. This is a bad workaround
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[`stories_count_${this.id}`] = 1;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[`total_stories_${this.id}`] = feedJson.channel.item.length;

                if (typeof this.#feedString === 'undefined' || this.#feedString !== newFeedString) {
                    this.#feedString = newFeedString;

                    this.renderTicker(feedJson);
                }
            })
            .fail(
                (
                    jqXHR: JQuery.jqXHR,
                    textStatus: string,
                    errorThrown: string,
                    msg?: string,
                    event?: Event
                ) => {
                    Log.Warning(
                        `Failed to get the feed for the Ticker Widget:${this.id}`,
                        null,
                        __fileline,
                        ReachAPI.createPromiseFailObject.apply(this, [
                            jqXHR,
                            textStatus,
                            errorThrown,
                            msg ?? null,
                            event ?? null,
                        ])
                    );
                }
            );
    }

    /**
     * Render the ticker
     *
     * @param feedJson The data to render. If no param, parse this.feedString
     * @private
     */
    private renderTicker(
        feedJson: TickerFeed = JSON.parse(this.#feedString ?? '{channel: {item: []}}')
    ): void {
        if (!this.#tickerShouldBeRendered) {
            return;
        }

        feedJson.channel.item.forEach((item: TickerFeedItem) => {
            item.title = this.decodeHtml(item.title);
            item.description = this.decodeHtml(item.description);
        });

        if (this.#useHardwareTicker) {
            this.renderBrightSignTicker(feedJson);
        } else {
            this.renderTemplateTicker(feedJson);
        }
    }

    /**
     * Get the render information for the ticker template
     * @protected
     */
    protected getRenderContext(): TickerTemplateData {
        return {
            wid: this.id,
            wobj: {
                widget: {
                    widgetsid: this.id,
                    config: {
                        css: this.config.css,
                    },
                },
            },
            node: this.config.node,
            Widget: { Id: this.id },
            stories: this.#tickerItems,
        };
    }

    /**
     * Render a ticker using a template
     *
     * @param feedJson
     * @private
     */
    private async renderTemplateTicker(feedJson: TickerFeed) {
        this.#tickerItems = feedJson.channel.item;

        try {
            await this.renderContent();
        } catch (error) {
            Log.Error(
                `Failed to render widget content (${this.id}:${this.name})`,
                null,
                __fileline,
                error
            );
        }
    }

    /**
     * Render a template using the BrightSign hardware ticker
     *
     * @param feedJson
     * @private
     */
    private renderBrightSignTicker(feedJson: TickerFeed): void {
        if (typeof feedJson === 'undefined' || typeof feedJson.channel.item !== 'object') {
            return;
        }

        if (this.#brightSignTicker) {
            this.#brightSignTicker.PopStrings(10000);
        } else {
            const zone = getZoneById(this.zoneId);

            this.#brightSignTicker = new BSTickerWrapper(
                Math.round(zone.x * glom.effectiveWindowScale),
                Math.round(zone.y * glom.effectiveWindowScale),
                Math.round(zone.width * glom.effectiveWindowScale),
                Math.round(zone.height * glom.effectiveWindowScale),
                0
            );

            this.#brightSignTicker.SetBackgroundColor(0, 255, 255, 255);
            this.#brightSignTicker.SetSeparatorCircle();

            this.#brightSignTicker.SetPixelsPerSecond(150);
        }

        feedJson.channel.item.forEach((item) => {
            this.#brightSignTicker?.AddString(item.title);
        });
    }

    /**
     * @inheritDoc
     */
    async afterFadeOut(): Promise<void> {
        if (this.#useHardwareTicker && this.#brightSignTicker?.bsTicker) {
            this.#brightSignTicker.stop();
        }
    }
}
